import { useState } from "react";
import Layout from "../components/Layout";
import { api } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Progress } from "../components/ui/progress";
import { 
  Calculator, 
  Loader2, 
  PieChart,
  AlertTriangle,
  TrendingUp,
  Shield,
  Target
} from "lucide-react";

const investmentGoalOptions = [
  { id: "retirement", label: "Retirement Planning" },
  { id: "education", label: "Education Funding" },
  { id: "wealth", label: "Wealth Accumulation" },
  { id: "income", label: "Income Generation" },
  { id: "preservation", label: "Capital Preservation" },
  { id: "emergency", label: "Emergency Fund" },
];

export default function RiskAssessment() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    investment_amount: 50000,
    time_horizon: 10,
    risk_tolerance: "moderate",
    investment_goals: [],
    current_age: 35,
    annual_income: 100000,
  });

  const handleGoalChange = (goalId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        investment_goals: [...prev.investment_goals, goalId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        investment_goals: prev.investment_goals.filter(g => g !== goalId)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.investment_goals.length === 0) {
      toast.error("Please select at least one investment goal");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await api.post("/tools/risk-assessment", formData);
      setResult(res.data);
      toast.success("Risk assessment complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Assessment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="risk-assessment-page">
        {/* Header */}
        <div>
          <p className="label-text mb-2">Tools</p>
          <h1 className="heading-xl">Risk Assessment Tool</h1>
          <p className="body-text mt-2">
            Evaluate your investment risk profile and get personalized asset allocation recommendations.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="w-12 h-12 rounded bg-[#8B0000] flex items-center justify-center mb-2">
                <Calculator className="h-6 w-6 text-white" />
              </div>
              <CardTitle>Investment Profile</CardTitle>
              <CardDescription>
                Provide information about your investment situation for a personalized assessment
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Investment Amount */}
                <div className="space-y-2">
                  <Label htmlFor="investmentAmount">Investment Amount ($)</Label>
                  <Input
                    id="investmentAmount"
                    type="number"
                    min="1000"
                    step="1000"
                    value={formData.investment_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, investment_amount: parseFloat(e.target.value) || 0 }))}
                    className="input-focus"
                    data-testid="investment-amount-input"
                    required
                  />
                </div>

                {/* Current Age */}
                <div className="space-y-2">
                  <Label htmlFor="currentAge">Current Age</Label>
                  <Input
                    id="currentAge"
                    type="number"
                    min="18"
                    max="100"
                    value={formData.current_age}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_age: parseInt(e.target.value) || 30 }))}
                    className="input-focus"
                    data-testid="current-age-input"
                    required
                  />
                </div>

                {/* Annual Income */}
                <div className="space-y-2">
                  <Label htmlFor="annualIncome">Annual Income ($)</Label>
                  <Input
                    id="annualIncome"
                    type="number"
                    min="0"
                    step="5000"
                    value={formData.annual_income}
                    onChange={(e) => setFormData(prev => ({ ...prev, annual_income: parseFloat(e.target.value) || 0 }))}
                    className="input-focus"
                    data-testid="annual-income-input"
                    required
                  />
                </div>

                {/* Time Horizon */}
                <div className="space-y-3">
                  <Label>Investment Time Horizon: {formData.time_horizon} years</Label>
                  <Slider
                    value={[formData.time_horizon]}
                    onValueChange={([value]) => setFormData(prev => ({ ...prev, time_horizon: value }))}
                    min={1}
                    max={40}
                    step={1}
                    className="w-full"
                    data-testid="time-horizon-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>1 year</span>
                    <span>20 years</span>
                    <span>40 years</span>
                  </div>
                </div>

                {/* Risk Tolerance */}
                <div className="space-y-2">
                  <Label htmlFor="riskTolerance">Risk Tolerance</Label>
                  <Select 
                    value={formData.risk_tolerance} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, risk_tolerance: value }))}
                  >
                    <SelectTrigger data-testid="risk-tolerance-select">
                      <SelectValue placeholder="Select risk tolerance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative - Minimize losses</SelectItem>
                      <SelectItem value="moderate">Moderate - Balance growth and safety</SelectItem>
                      <SelectItem value="aggressive">Aggressive - Maximize growth</SelectItem>
                      <SelectItem value="very_aggressive">Very Aggressive - High risk/reward</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Investment Goals */}
                <div className="space-y-3">
                  <Label>Investment Goals (select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {investmentGoalOptions.map((goal) => (
                      <div key={goal.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={goal.id}
                          checked={formData.investment_goals.includes(goal.id)}
                          onCheckedChange={(checked) => handleGoalChange(goal.id, checked)}
                          data-testid={`goal-${goal.id}`}
                        />
                        <Label htmlFor={goal.id} className="text-sm font-normal cursor-pointer">
                          {goal.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={loading}
                  data-testid="submit-assessment"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="h-4 w-4 mr-2" />
                      Calculate Risk Profile
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <div>
            {result ? (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-[#8B0000]" />
                    Your Risk Assessment Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Risk Score */}
                  <div className="text-center p-6 bg-slate-50 border border-slate-200">
                    <p className="label-text mb-2">Risk Score</p>
                    <p className="text-5xl font-bold text-[#8B0000]">{result.risk_score}</p>
                    <p className="text-sm text-slate-500 mt-1">out of 10</p>
                    <Progress value={result.risk_score * 10} className="mt-4 h-2" />
                  </div>

                  {/* Risk Profile */}
                  <div className="p-4 border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <Target className="h-5 w-5 text-[#8B0000]" />
                      <h3 className="font-semibold">Risk Profile: {result.risk_profile}</h3>
                    </div>
                    <p className="text-sm text-slate-600">{result.description}</p>
                  </div>

                  {/* Suggested Allocation */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <PieChart className="h-4 w-4" />
                      Suggested Asset Allocation
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Stocks</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-[#8B0000] h-2 rounded-full" 
                              style={{ width: `${result.suggested_allocation.stocks}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{result.suggested_allocation.stocks}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Bonds</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${result.suggested_allocation.bonds}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{result.suggested_allocation.bonds}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Cash</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${result.suggested_allocation.cash}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{result.suggested_allocation.cash}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Investment Capacity */}
                  <div className="p-4 bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-emerald-700" />
                      <span className="text-sm font-medium text-emerald-700">Suggested Annual Investment</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-800">
                      ${result.investment_capacity_annual.toLocaleString()}
                    </p>
                    <p className="text-xs text-emerald-600">Based on 20% of annual income</p>
                  </div>

                  {/* Disclaimer */}
                  <div className="p-4 bg-amber-50 border border-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{result.disclaimer}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200 bg-slate-50 h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center text-slate-500">
                  <Calculator className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Complete the form to see your risk assessment results</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Warning */}
        <div className="p-4 bg-amber-50 border border-amber-200 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Educational Tool Only</p>
            <p>
              This risk assessment tool provides general educational guidance and should not be considered 
              personalized investment advice. Individual circumstances vary, and investment decisions should 
              be made with qualified financial professionals. Contact Colin Darke at{" "}
              <a href="mailto:colindarke@complisun.com" className="underline">colindarke@complisun.com</a> for 
              personalized guidance.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
